export { useMediaQuery } from "./useMediaQuery.ts";
import type { FeatureArea } from "@bundle/contracts";
import { ChannelViewModel, Features, Roles, ThreadSummaryViewModel, type FeatureAccess, type ChannelViewState, type ThreadSummaryState, type SessionState, type SidebarState, type ViewModel, type WorkspaceState } from "@bundle/core";
import { useCallback, useSyncExternalStore } from "react";
import { useSessionViewModel, useWorkspaceContext } from "../context/AppContext.tsx";
import { useOwned } from "./owned.ts";

/** Subscribes to a view model's state. */
export function useViewModel<S>(vm: ViewModel<S>): S {
  return useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot);
}

export function useSession(): { vm: ReturnType<typeof useSessionViewModel>; state: SessionState } {
  const vm = useSessionViewModel();
  return { vm, state: useViewModel(vm) };
}

export function useWorkspace(): { vm: ReturnType<typeof useWorkspaceContext>["workspace"]; state: WorkspaceState; slug: string } {
  const ctx = useWorkspaceContext();
  return { vm: ctx.workspace, state: useViewModel(ctx.workspace), slug: ctx.slug };
}

/**
 * What I get of each product area (core logic/features): `full`, `read` (off, but owners and admins may still look)
 * or `none` (off: hidden). Follows `workspace.updated` live.
 */
export function useFeatureAccess(): (area: FeatureArea) => FeatureAccess {
  const { vm, state } = useWorkspace();
  const w = state.cache?.workspace;
  const me = state.cache?.members[state.meId];
  const manager = me !== undefined && Roles.isManager(me, vm.deps.clock.now());
  const features = w?.features;
  const adminAccess = w?.featureAdminAccess;
  return useCallback((area: FeatureArea) => Features.featureAccess(features, area, manager, adminAccess), [features, adminAccess, manager]);
}

export function useSidebar(): { vm: ReturnType<typeof useWorkspaceContext>["sidebar"]; state: SidebarState } {
  const ctx = useWorkspaceContext();
  return { vm: ctx.sidebar, state: useViewModel(ctx.sidebar) };
}

/** The view model for one conversation, shared by everything showing it and disposed when unused. */
export function useChannel(channelId: string, threadRootId?: string): { vm: ChannelViewModel; state: ChannelViewState } {
  const { workspace } = useWorkspaceContext();
  const key = `channel:${workspace.wsId}:${workspace.state.meId}:${channelId}:${threadRootId ?? ""}`;
  const create = useCallback(() => new ChannelViewModel(workspace, channelId, threadRootId), [workspace, channelId, threadRootId]);
  const vm = useOwned(key, create);
  const state = useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot);
  return { vm, state };
}

/** The AI TL;DR card of a thread (core ThreadSummaryViewModel), next to `useChannel(channelId, rootId)`. */
export function useThreadSummary(channelId: string, rootId: string): { vm: ThreadSummaryViewModel; state: ThreadSummaryState } {
  const { workspace } = useWorkspaceContext();
  const thread = useChannel(channelId, rootId).vm;
  const create = useCallback(() => new ThreadSummaryViewModel(workspace, thread, rootId), [workspace, thread, rootId]);
  const vm = useOwned(`threadSummary:${workspace.wsId}:${workspace.state.meId}:${channelId}:${rootId}`, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}

export { useOwned };
export { useDatabase, useDatabaseRow } from "./database.ts";
export { usePageTree, usePageTreeViewModel } from "./pages.ts";
export { usePage } from "./page.ts";
export { useFile, useFileActions } from "./files.ts";
export { useBrowseChannels, useChannelMembers, useChannelSettings, useCreateChannel, useNewMessage } from "./channels.ts";
export { huddleTitle, todayIso, useFollow, useHuddlesArea } from "./huddlesArea.ts";
